/**
 * @file po-auto-planner.engine.ts
 * @description Domain Engine for Production Order Auto-Material Generation (Paper, Lamination Film,
 * Corrugated/Paper/Chipboard Mounting Substrates) and 5-Subtable Synchronization with Zero Hard-Lock.
 *
 * Domain Invariants:
 * 1. Lamination Film Roll Width Matching (650x430mm example):
 *    - Safety margin: 5mm narrower than print sheet edge to prevent adhesive bleeding onto rollers.
 *    - Priority 1 (Horizontal / Khổ ngang): Roll width = max(W, L) - 5mm (e.g. 645mm), feed = min(W, L) (430mm) if in stock (> 0).
 *    - Priority 2 (Vertical / Khổ dọc): Roll width = min(W, L) - 5mm (e.g. 425mm), feed = max(W, L) (650mm) if in stock (> 0).
 *    - Priority 3 (Auto-Generate Code): Default to Horizontal width max(W, L) - 5mm (e.g. VT-MANG-MO-645 / VT-MANG-BONG-645).
 * 2. Mounting Substrates (Bồi sóng, Bồi giấy, Bồi chipboard):
 *    - Match print sheet size WxL (e.g. 650x430mm) in stock (> 0), or auto-generate semantic code
 *      (VT-SONG-E-650x430, VT-GIAY-BOI-D250-650x430, VT-CHIP-1200-650x430).
 * 3. Zero Hard-Lock & User Customization Protection:
 *    - Rows with `isUserCustomized: true` are preserved across reactive re-calculations.
 */

export interface SheetDimensionsMm {
    rawWidthMm: number;
    rawLengthMm: number;
    longEdgeMm: number;
    shortEdgeMm: number;
    formattedMm: string;
    canonicalMm: string;
}

export type LaminationFilmType = 'MANG_MO' | 'MANG_BONG' | 'MANG_METALIZE' | 'MANG_NHUNG';
export type FeedDirection = 'HORIZONTAL' | 'VERTICAL';
export type MaterialMatchSource =
    | 'STOCK_HORIZONTAL'
    | 'STOCK_VERTICAL'
    | 'STOCK_EXACT'
    | 'CATALOG_ZERO_STOCK'
    | 'AUTO_GENERATED';

export interface CatalogMaterialItem {
    id: string;
    materialId?: string;
    code?: string;
    name: string;
    category?: string;
    subType?: string | null;
    gsm?: string | number | null;
    width?: string | number | null;
    length?: string | number | null;
    specs?: string | null;
    unit?: string | null;
    currentStock?: string | number | null;
}

export interface AutoPlannedMaterialRow {
    id?: string;
    originalMaterialId: string;
    plannedMaterialId: string;
    materialCode: string;
    materialName: string;
    materialCategory: 'PAPER' | 'FILM' | 'GLUE' | 'INK' | 'OTHER';
    subType?: string;
    isPrint: boolean;
    sheetsRequired: number;
    wastageSheets: number;
    totalSheets: number;
    unit: string;
    _gsm: string;
    _size: string;
    widthMm?: number;
    lengthMm?: number;
    // Lamination specific conversions
    rollWidthMm?: number;
    feedLengthMm?: number;
    feedDirection?: FeedDirection;
    sides?: number;
    linearMeters?: number;
    weightKg?: number;
    // Metadata & User Override flags
    isAutoGenerated: boolean;
    matchSource: MaterialMatchSource;
    availableStock: number;
    splitReason?: string;
    isUserCustomized?: boolean;
    autoRuleKey?: string;
}

export interface AutoPlannedBomRow {
    materialId: string;
    materialCode: string;
    materialName: string;
    materialCategory: 'PAPER' | 'FILM' | 'GLUE' | 'INK' | 'OTHER';
    specification?: string;
    unit: string;
    quantity: number;
    qtyRequired: number;
    wastagePercent: number;
    totalQuantity: number;
    _gsm?: string;
    _size?: string;
    linearMeters?: number;
    weightKg?: number;
    notes?: string;
    isAutoGenerated?: boolean;
    isUserCustomized?: boolean;
    autoRuleKey?: string;
}

/**
 * Parses a raw sheet size string (e.g. "650x430", "650x430mm", "65x43cm", "65 x 43")
 * into normalized millimeter dimensions.
 */
export function parseSheetDimensionsMm(rawSize?: string | null): SheetDimensionsMm | null {
    if (!rawSize || typeof rawSize !== 'string') return null;
    const cleaned = rawSize.trim().toLowerCase();
    if (!cleaned) return null;

    const isExplicitCm = cleaned.includes('cm') && !cleaned.includes('mm');
    const match = cleaned.match(/(\d+(?:[.,]\d+)?)\s*[x*×]\s*(\d+(?:[.,]\d+)?)/i);
    if (!match) return null;

    let w = parseFloat(match[1].replace(',', '.'));
    let l = parseFloat(match[2].replace(',', '.'));
    if (!Number.isFinite(w) || !Number.isFinite(l) || w <= 0 || l <= 0) return null;

    // Heuristic: in offset printing, sheet dimensions <= 160 without 'mm' are in centimeters (e.g. 65x43 -> 650x430mm)
    if (isExplicitCm || (w <= 160 && l <= 160 && !cleaned.includes('mm'))) {
        w = Math.round(w * 10);
        l = Math.round(l * 10);
    } else {
        w = Math.round(w);
        l = Math.round(l);
    }

    const longEdgeMm = Math.max(w, l);
    const shortEdgeMm = Math.min(w, l);

    return {
        rawWidthMm: w,
        rawLengthMm: l,
        longEdgeMm,
        shortEdgeMm,
        formattedMm: `${w}x${l}`,
        canonicalMm: `${longEdgeMm}x${shortEdgeMm}`,
    };
}

/**
 * Strips Vietnamese diacritics and lowercases for resilient keyword matching.
 */
export function normalizeViText(input?: string | null): string {
    if (!input) return '';
    return input
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .toLowerCase()
        .trim();
}

/**
 * Detects whether Lamination (Cán màng) is required and extracts film type & number of sides.
 */
export function detectLaminationRequirement(params: {
    processes?: string[];
    specification?: string | null;
    finishingSpecs?: Record<string, any> | null;
    orderSources?: Array<Record<string, any>>;
    notes?: string | null;
}): { required: boolean; filmType: LaminationFilmType; sides: number } {
    const tokens: string[] = [];

    if (Array.isArray(params.processes)) {
        tokens.push(...params.processes);
    }
    if (params.specification) tokens.push(params.specification);
    if (params.notes) tokens.push(params.notes);
    if (params.finishingSpecs?.maMang) tokens.push(String(params.finishingSpecs.maMang));
    if (params.finishingSpecs?.trangPhu) tokens.push(String(params.finishingSpecs.trangPhu));

    if (Array.isArray(params.orderSources)) {
        for (const src of params.orderSources) {
            if (Array.isArray(src.processes)) tokens.push(...src.processes);
            else if (typeof src.processes === 'string') tokens.push(src.processes);
            if (src.lamination) tokens.push(String(src.lamination));
            if (src.surfaceFinish) tokens.push(String(src.surfaceFinish));
            if (src.PrintFinishingProcess) tokens.push(String(src.PrintFinishingProcess));
            if (src.specification) tokens.push(String(src.specification));
            // Ghi chú thành phẩm theo dòng sản phẩm (thay khối thông số gia công cũ)
            if (src.finishingNotes) tokens.push(String(src.finishingNotes));
        }
    }

    const combinedRaw = tokens.join(' | ');
    const norm = normalizeViText(combinedRaw);

    const hasExplicitNegative =
        norm === 'khong' ||
        norm === 'none' ||
        (norm.includes('khong can mang') && !norm.includes('can mang mo') && !norm.includes('can mang bong'));

    if (hasExplicitNegative) {
        return { required: false, filmType: 'MANG_MO', sides: 1 };
    }

    const isLaminationTriggered =
        norm.includes('can_mang') ||
        norm.includes('can mang') ||
        norm.includes('lamination') ||
        norm.includes('mang mo') ||
        norm.includes('mang bong') ||
        norm.includes('mang metalize') ||
        norm.includes('mang nhung') ||
        norm.includes('bopp') ||
        norm.includes('vt-mang-');

    if (!isLaminationTriggered) {
        return { required: false, filmType: 'MANG_MO', sides: 1 };
    }

    let filmType: LaminationFilmType = 'MANG_MO';
    if (norm.includes('metalize') || norm.includes('mang met') || norm.includes('vt-mang-met')) {
        filmType = 'MANG_METALIZE';
    } else if (norm.includes('nhung') || norm.includes('soft touch') || norm.includes('vt-mang-nhung')) {
        filmType = 'MANG_NHUNG';
    } else if (norm.includes('bong') || norm.includes('gloss') || norm.includes('can_mang_bong') || norm.includes('vt-mang-bong')) {
        filmType = 'MANG_BONG';
    } else {
        filmType = 'MANG_MO';
    }

    const sides =
        norm.includes('2 mat') || norm.includes('hai mat') || norm.includes('2-side') || norm.includes('2 side')
            ? 2
            : 1;

    return { required: true, filmType, sides };
}

function extractMaterialWidthMm(mat: CatalogMaterialItem): number | null {
    if (mat.width !== undefined && mat.width !== null && String(mat.width).trim() !== '') {
        const num = Number(mat.width);
        if (Number.isFinite(num) && num > 0) {
            return num <= 160 ? Math.round(num * 10) : Math.round(num);
        }
    }
    const text = `${mat.materialId || ''} ${mat.code || ''} ${mat.name || ''} ${mat.specs || ''}`;
    const mmMatch = text.match(/(?:kho|width|w|-|\s)(\d{3,4})\s*(?:mm)?\b/i);
    if (mmMatch) {
        const parsed = parseInt(mmMatch[1], 10);
        if (parsed >= 150 && parsed <= 2000) return parsed;
    }
    const cmMatch = text.match(/(\d{2}(?:\.\d+)?)\s*cm\b/i);
    if (cmMatch) {
        return Math.round(parseFloat(cmMatch[1]) * 10);
    }
    return null;
}

function matchesCatalogFilmType(mat: CatalogMaterialItem, filmType: LaminationFilmType): boolean {
    const text = normalizeViText(
        `${mat.materialId || ''} ${mat.code || ''} ${mat.name || ''} ${mat.subType || ''} ${mat.specs || ''}`
    );
    const isFilm =
        mat.category === 'FILM' ||
        text.includes('mang') ||
        text.includes('bopp') ||
        text.includes('film') ||
        text.includes('metalize');
    if (!isFilm) return false;

    if (filmType === 'MANG_METALIZE') return text.includes('metalize') || text.includes('mang met');
    if (filmType === 'MANG_NHUNG') return text.includes('nhung') || text.includes('soft');
    if (filmType === 'MANG_BONG') return text.includes('bong') || text.includes('gloss');
    // MANG_MO
    return text.includes('mo') || text.includes('matte') || (!text.includes('bong') && !text.includes('metalize') && !text.includes('nhung'));
}

function getFilmMeta(filmType: LaminationFilmType, rollWidthMm: number) {
    switch (filmType) {
        case 'MANG_BONG':
            return {
                code: `VT-MANG-BONG-${rollWidthMm}`,
                name: `Màng BOPP Bóng khổ ${rollWidthMm}mm`,
                subType: 'Màng BOPP Bóng',
                gsm: '18 mic',
                densityFactor: 0.018,
            };
        case 'MANG_METALIZE':
            return {
                code: `VT-MANG-MET-${rollWidthMm}`,
                name: `Màng Metalize khổ ${rollWidthMm}mm`,
                subType: 'Màng Metalize',
                gsm: '24 mic',
                densityFactor: 0.024,
            };
        case 'MANG_NHUNG':
            return {
                code: `VT-MANG-NHUNG-${rollWidthMm}`,
                name: `Màng Nhung khổ ${rollWidthMm}mm`,
                subType: 'Màng Nhung',
                gsm: '28 mic',
                densityFactor: 0.028,
            };
        case 'MANG_MO':
        default:
            return {
                code: `VT-MANG-MO-${rollWidthMm}`,
                name: `Màng BOPP Mờ khổ ${rollWidthMm}mm`,
                subType: 'Màng BOPP Mờ',
                gsm: '18 mic',
                densityFactor: 0.018,
            };
    }
}

/**
 * Calculates Lamination Film linear meters (m) and weight (kg) from sheet count and roll dimensions.
 */
export function calculateFilmMetrics(params: {
    rollWidthMm: number;
    feedLengthMm: number;
    totalSheets: number;
    filmType?: LaminationFilmType;
}): { linearMeters: number; weightKg: number } {
    const { rollWidthMm, feedLengthMm, totalSheets, filmType = 'MANG_MO' } = params;
    if (rollWidthMm <= 0 || feedLengthMm <= 0 || totalSheets <= 0) {
        return { linearMeters: 0, weightKg: 0 };
    }
    const { densityFactor } = getFilmMeta(filmType, rollWidthMm);
    const linearMeters = Math.ceil((totalSheets * feedLengthMm) / 1000);
    const areaSqMeters = (rollWidthMm * feedLengthMm * totalSheets) / 1_000_000;
    const weightKg = Number((areaSqMeters * densityFactor).toFixed(2));
    return { linearMeters, weightKg };
}

/**
 * Plans the optimal Lamination Film material row according to the 5mm safety margin rule:
 * - Priority 1: Horizontal roll (max(W, L) - 5mm, e.g. 645mm for 650x430) with stock > 0
 * - Priority 2: Vertical roll (min(W, L) - 5mm, e.g. 425mm for 650x430) with stock > 0
 * - Priority 3: Auto-generate new material code at Horizontal roll width (max(W, L) - 5mm = 645mm)
 */
export function planLaminationFilmMaterial(params: {
    sheetSize: string;
    requiredSheets: number;
    wastageSheets: number;
    filmType: LaminationFilmType;
    sides?: number;
    catalogMaterials?: CatalogMaterialItem[];
    safetyMarginMm?: number;
}): AutoPlannedMaterialRow | null {
    const dims = parseSheetDimensionsMm(params.sheetSize);
    if (!dims) return null;

    const margin = params.safetyMarginMm ?? 5;
    const sides = Math.max(1, params.sides ?? 1);
    const catalog = params.catalogMaterials || [];

    const horizWidthMm = Math.max(10, dims.longEdgeMm - margin);  // e.g. 650 - 5 = 645mm
    const horizFeedMm = dims.shortEdgeMm;                         // e.g. 430mm

    const vertWidthMm = Math.max(10, dims.shortEdgeMm - margin);  // e.g. 430 - 5 = 425mm
    const vertFeedMm = dims.longEdgeMm;                           // e.g. 650mm

    const findMatchingMaterial = (targetWidthMm: number, requireStock: boolean): CatalogMaterialItem | undefined => {
        return catalog.find((mat) => {
            if (!matchesCatalogFilmType(mat, params.filmType)) return false;
            const matWidth = extractMaterialWidthMm(mat);
            if (matWidth !== targetWidthMm) return false;
            const stock = Number(mat.currentStock || 0);
            return requireStock ? stock > 0 : true;
        });
    };

    // Priority 1: Horizontal roll (645mm) with stock > 0
    let matchedMat = findMatchingMaterial(horizWidthMm, true);
    let chosenRollWidthMm = horizWidthMm;
    let chosenFeedLengthMm = horizFeedMm;
    let chosenDirection: FeedDirection = 'HORIZONTAL';
    let matchSource: MaterialMatchSource = 'STOCK_HORIZONTAL';

    if (!matchedMat) {
        // Priority 2: Vertical roll (425mm) with stock > 0
        const vertStockMat = findMatchingMaterial(vertWidthMm, true);
        if (vertStockMat) {
            matchedMat = vertStockMat;
            chosenRollWidthMm = vertWidthMm;
            chosenFeedLengthMm = vertFeedMm;
            chosenDirection = 'VERTICAL';
            matchSource = 'STOCK_VERTICAL';
        } else {
            // Priority 3: Check catalog with 0 stock (prefer horizontal 645mm), else auto-generate 645mm
            const zeroStockHoriz = findMatchingMaterial(horizWidthMm, false);
            if (zeroStockHoriz) {
                matchedMat = zeroStockHoriz;
                chosenRollWidthMm = horizWidthMm;
                chosenFeedLengthMm = horizFeedMm;
                chosenDirection = 'HORIZONTAL';
                matchSource = 'CATALOG_ZERO_STOCK';
            } else {
                matchedMat = undefined;
                chosenRollWidthMm = horizWidthMm;
                chosenFeedLengthMm = horizFeedMm;
                chosenDirection = 'HORIZONTAL';
                matchSource = 'AUTO_GENERATED';
            }
        }
    }

    const sheetsRequired = Math.max(0, Math.round(params.requiredSheets * sides));
    const wastageSheets = Math.max(0, Math.round(params.wastageSheets * sides));
    const totalSheets = sheetsRequired + wastageSheets;

    const meta = getFilmMeta(params.filmType, chosenRollWidthMm);
    const { linearMeters, weightKg } = calculateFilmMetrics({
        rollWidthMm: chosenRollWidthMm,
        feedLengthMm: chosenFeedLengthMm,
        totalSheets,
        filmType: params.filmType,
    });

    const materialCode = matchedMat?.materialId || matchedMat?.code || meta.code;
    const materialName = matchedMat?.name || meta.name;
    const materialId = matchedMat?.id || materialCode;
    const availableStock = Number(matchedMat?.currentStock || 0);

    const directionLabel = chosenDirection === 'HORIZONTAL' ? 'Khổ ngang -5mm' : 'Khổ dọc -5mm';

    return {
        originalMaterialId: materialId,
        plannedMaterialId: materialId,
        materialCode,
        materialName,
        materialCategory: 'FILM',
        subType: meta.subType,
        isPrint: false,
        sheetsRequired,
        wastageSheets,
        totalSheets,
        unit: 'Tờ',
        _gsm: String(matchedMat?.gsm || meta.gsm),
        _size: `${chosenRollWidthMm}x${chosenFeedLengthMm}`,
        widthMm: chosenRollWidthMm,
        lengthMm: chosenFeedLengthMm,
        rollWidthMm: chosenRollWidthMm,
        feedLengthMm: chosenFeedLengthMm,
        feedDirection: chosenDirection,
        sides,
        linearMeters,
        weightKg,
        isAutoGenerated: !matchedMat,
        matchSource,
        availableStock,
        splitReason: `${directionLabel} (${chosenRollWidthMm}mm, bước cắt ${chosenFeedLengthMm}mm) • ~${linearMeters}m (${weightKg}kg)`,
        isUserCustomized: false,
        autoRuleKey: `LAMINATION_${params.filmType}`,
    };
}

export interface DetectedMountingSpec {
    kind: 'BOI_SONG' | 'BOI_GIAY' | 'BOI_CHIPBOARD';
    fluteType?: string;   // e.g. 'E', 'B', 'C', 'F', 'AB', 'BC'
    paperCode?: string;   // e.g. 'D250', 'D300', 'K250'
    gsmValue?: string;    // e.g. '250', '1200'
}

/**
 * Detects mounting requirements (Bồi sóng, Bồi giấy, Bồi chipboard/carton lạnh) from PO specs & processes.
 */
export function detectMountingRequirements(params: {
    processes?: string[];
    specification?: string | null;
    finishingSpecs?: Record<string, any> | null;
    orderSources?: Array<Record<string, any>>;
    notes?: string | null;
}): DetectedMountingSpec[] {
    const tokens: string[] = [];
    if (Array.isArray(params.processes)) tokens.push(...params.processes);
    if (params.specification) tokens.push(params.specification);
    if (params.notes) tokens.push(params.notes);
    if (params.finishingSpecs?.boi) tokens.push(String(params.finishingSpecs.boi));

    if (Array.isArray(params.orderSources)) {
        for (const src of params.orderSources) {
            if (Array.isArray(src.processes)) tokens.push(...src.processes);
            else if (typeof src.processes === 'string') tokens.push(src.processes);
            if (src.boi) tokens.push(String(src.boi));
            if (src.fluteType) tokens.push(`boi song ${src.fluteType}`);
            if (src.mountingSpec) tokens.push(String(src.mountingSpec));
            if (src.specification) tokens.push(String(src.specification));
            if (src.finishingNotes) tokens.push(String(src.finishingNotes));
        }
    }

    const combinedRaw = tokens.join(' | ');
    const norm = normalizeViText(combinedRaw);
    if (!norm) return [];

    const results: DetectedMountingSpec[] = [];

    // 1. Check Corrugated Flute Mounting (Bồi sóng)
    const hasBoiSong =
        norm.includes('boi_song') ||
        norm.includes('boi song') ||
        norm.includes('flute') ||
        norm.includes('song e') ||
        norm.includes('song b') ||
        norm.includes('song c') ||
        norm.includes('song f') ||
        norm.includes('song ab') ||
        norm.includes('song bc') ||
        norm.includes('vt-song-');

    if (hasBoiSong) {
        let fluteType = 'E';
        const fluteMatch = norm.match(/\bsong\s+(ab|bc|be|e|b|c|f|a)\b/i) || norm.match(/vt-song-(ab|bc|be|e|b|c|f|a)\b/i);
        if (fluteMatch) {
            fluteType = fluteMatch[1].toUpperCase();
        }
        results.push({
            kind: 'BOI_SONG',
            fluteType,
            gsmValue: `Sóng ${fluteType}`,
        });
    }

    // 2. Check Chipboard / Rigid Box Mounting (Bồi chipboard / carton lạnh / bìa cứng)
    const hasBoiChipboard =
        norm.includes('chipboard') ||
        norm.includes('carton lanh') ||
        norm.includes('bia cung') ||
        norm.includes('boi_chipboard') ||
        norm.includes('boi_carton') ||
        norm.includes('vt-chip-');

    if (hasBoiChipboard) {
        const gsmMatch = norm.match(/(?:chipboard|carton lanh|bia cung|vt-chip-)\s*(\d{3,4})/i);
        const gsmValue = gsmMatch ? gsmMatch[1] : '1200';
        results.push({
            kind: 'BOI_CHIPBOARD',
            gsmValue,
        });
    }

    // 3. Check Paper-to-Paper Mounting (Bồi giấy Duplex / Kraft / Ivory)
    const hasBoiGiay =
        norm.includes('boi_giay') ||
        norm.includes('boi giay') ||
        norm.includes('boi duplex') ||
        norm.includes('boi kraft') ||
        norm.includes('boi ivory') ||
        norm.includes('vt-giay-boi-') ||
        (/\bboi\s+(d\d{3}|k\d{3}|i\d{3}|c\d{3})\b/i.test(norm)) ||
        // Generic 'BOI' or 'MOUNTING' when neither sóng nor chipboard matched
        (results.length === 0 && (norm.includes('boi') || norm.includes('mounting')) && !norm.includes('khong boi'));

    if (hasBoiGiay) {
        const codeMatch = norm.match(/\b(d\d{3}|k\d{3}|i\d{3}|c\d{3}|duplex\s*\d{3}|kraft\s*\d{3})\b/i);
        let paperCode = 'D250';
        let gsmValue = '250';
        if (codeMatch) {
            const rawCode = codeMatch[1].toUpperCase().replace(/\s+/g, '');
            if (rawCode.startsWith('DUPLEX')) {
                gsmValue = rawCode.replace('DUPLEX', '');
                paperCode = `D${gsmValue}`;
            } else if (rawCode.startsWith('KRAFT')) {
                gsmValue = rawCode.replace('KRAFT', '');
                paperCode = `K${gsmValue}`;
            } else {
                paperCode = rawCode;
                const digits = rawCode.match(/\d{3}/);
                if (digits) gsmValue = digits[0];
            }
        }
        results.push({
            kind: 'BOI_GIAY',
            paperCode,
            gsmValue,
        });
    }

    return results;
}

function matchesSheetDimensions(mat: CatalogMaterialItem, dims: SheetDimensionsMm): boolean {
    const matDims = parseSheetDimensionsMm(
        mat.width && mat.length ? `${mat.width}x${mat.length}` : `${mat.name || ''} ${mat.specs || ''} ${mat.materialId || ''}`
    );
    if (!matDims) return false;
    return (
        (matDims.longEdgeMm === dims.longEdgeMm && matDims.shortEdgeMm === dims.shortEdgeMm)
    );
}

/**
 * Plans Mounting Substrate Materials (Bồi sóng, Bồi giấy, Bồi chipboard) matching the print sheet size.
 * Checks inventory first (`currentStock > 0`), or auto-generates semantic material codes if not in stock.
 */
export function planMountingSubstrateMaterials(params: {
    sheetSize: string;
    requiredSheets: number;
    wastageSheets: number;
    mountingSpecs: DetectedMountingSpec[];
    catalogMaterials?: CatalogMaterialItem[];
}): AutoPlannedMaterialRow[] {
    const dims = parseSheetDimensionsMm(params.sheetSize);
    if (!dims || !params.mountingSpecs.length) return [];

    const catalog = params.catalogMaterials || [];
    const sheetsRequired = Math.max(0, Math.round(params.requiredSheets));
    const wastageSheets = Math.max(0, Math.round(params.wastageSheets));
    const totalSheets = sheetsRequired + wastageSheets;

    return params.mountingSpecs.map((spec): AutoPlannedMaterialRow => {
        if (spec.kind === 'BOI_SONG') {
            const flute = (spec.fluteType || 'E').toUpperCase();
            const findFluteMat = (requireStock: boolean) =>
                catalog.find((m) => {
                    const code = String(m.materialId || m.code || '').toUpperCase();
                    if (
                        code.startsWith('VT-GIAY-') ||
                        code.startsWith('VT-MANG-') ||
                        code.startsWith('VT-CHIP-')
                    ) {
                        return false;
                    }
                    const nameNorm = normalizeViText(String(m.name || ''));
                    if (nameNorm.startsWith('giay in ')) return false;
                    const text = normalizeViText(`${m.materialId || ''} ${m.code || ''} ${m.name || ''} ${m.subType || ''} ${m.specs || ''}`);
                    const hasFlute = text.includes(`song ${flute.toLowerCase()}`) || text.includes(`vt-song-${flute.toLowerCase()}`);
                    if (!hasFlute) return false;
                    if (!matchesSheetDimensions(m, dims)) return false;
                    return requireStock ? Number(m.currentStock || 0) > 0 : true;
                });

            const stockMat = findFluteMat(true);
            const catalogMat = stockMat || findFluteMat(false);
            const autoCode = `VT-SONG-${flute}-${dims.formattedMm}`;
            const autoName = `Giấy sóng ${flute} bồi khổ ${dims.formattedMm}mm`;

            const materialCode = catalogMat?.materialId || catalogMat?.code || autoCode;
            const materialName = catalogMat?.name || autoName;
            const materialId = catalogMat?.id || materialCode;

            return {
                originalMaterialId: materialId,
                plannedMaterialId: materialId,
                materialCode,
                materialName,
                materialCategory: 'PAPER',
                subType: `Sóng ${flute}`,
                isPrint: false,
                sheetsRequired,
                wastageSheets,
                totalSheets,
                unit: catalogMat?.unit || 'Tờ',
                _gsm: String(catalogMat?.gsm || `Sóng ${flute}`),
                _size: dims.formattedMm,
                widthMm: dims.rawWidthMm,
                lengthMm: dims.rawLengthMm,
                isAutoGenerated: !catalogMat,
                matchSource: stockMat ? 'STOCK_EXACT' : catalogMat ? 'CATALOG_ZERO_STOCK' : 'AUTO_GENERATED',
                availableStock: Number(catalogMat?.currentStock || 0),
                splitReason: `Vật tư bồi sóng ${flute} theo khổ giấy in ${dims.formattedMm}mm`,
                isUserCustomized: false,
                autoRuleKey: `MOUNTING_BOI_SONG_${flute}`,
            };
        }

        if (spec.kind === 'BOI_CHIPBOARD') {
            const gsm = spec.gsmValue || '1200';
            const findChipMat = (requireStock: boolean) =>
                catalog.find((m) => {
                    const code = String(m.materialId || m.code || '').toUpperCase();
                    if (
                        code.startsWith('VT-GIAY-') ||
                        code.startsWith('VT-SONG-') ||
                        code.startsWith('VT-MANG-')
                    ) {
                        return false;
                    }
                    const text = normalizeViText(`${m.materialId || ''} ${m.code || ''} ${m.name || ''} ${m.subType || ''} ${m.specs || ''}`);
                    const isChip = text.includes('chipboard') || text.includes('carton lanh') || text.includes('vt-chip-');
                    if (!isChip) return false;
                    const matchesGsm = String(m.gsm || '').includes(gsm) || text.includes(gsm);
                    if (!matchesGsm) return false;
                    if (!matchesSheetDimensions(m, dims)) return false;
                    return requireStock ? Number(m.currentStock || 0) > 0 : true;
                });

            const stockMat = findChipMat(true);
            const catalogMat = stockMat || findChipMat(false);
            const autoCode = `VT-CHIP-${gsm}-${dims.formattedMm}`;
            const autoName = `Chipboard carton lạnh ${gsm}gsm khổ ${dims.formattedMm}mm`;

            const materialCode = catalogMat?.materialId || catalogMat?.code || autoCode;
            const materialName = catalogMat?.name || autoName;
            const materialId = catalogMat?.id || materialCode;

            return {
                originalMaterialId: materialId,
                plannedMaterialId: materialId,
                materialCode,
                materialName,
                materialCategory: 'PAPER',
                subType: 'Chipboard / Carton lạnh',
                isPrint: false,
                sheetsRequired,
                wastageSheets,
                totalSheets,
                unit: catalogMat?.unit || 'Tấm',
                _gsm: String(catalogMat?.gsm || gsm),
                _size: dims.formattedMm,
                widthMm: dims.rawWidthMm,
                lengthMm: dims.rawLengthMm,
                isAutoGenerated: !catalogMat,
                matchSource: stockMat ? 'STOCK_EXACT' : catalogMat ? 'CATALOG_ZERO_STOCK' : 'AUTO_GENERATED',
                availableStock: Number(catalogMat?.currentStock || 0),
                splitReason: `Vật tư bồi Chipboard ${gsm}gsm theo khổ giấy in ${dims.formattedMm}mm`,
                isUserCustomized: false,
                autoRuleKey: `MOUNTING_BOI_CHIPBOARD_${gsm}`,
            };
        }

        // Default: BOI_GIAY
        const paperCode = (spec.paperCode || 'D250').toUpperCase();
        const gsm = spec.gsmValue || '250';
        const findPaperMat = (requireStock: boolean) =>
            catalog.find((m) => {
                const code = String(m.materialId || m.code || '').toUpperCase();
                if (
                    code.startsWith('VT-SONG-') ||
                    code.startsWith('VT-MANG-') ||
                    code.startsWith('VT-CHIP-') ||
                    (code.startsWith('VT-GIAY-') && !code.startsWith('VT-GIAY-BOI-'))
                ) {
                    return false;
                }
                const text = normalizeViText(`${m.materialId || ''} ${m.code || ''} ${m.name || ''} ${m.subType || ''} ${m.specs || ''}`);
                const hasPaper = text.includes(paperCode.toLowerCase()) || (text.includes('duplex') && text.includes(gsm));
                if (!hasPaper) return false;
                if (!matchesSheetDimensions(m, dims)) return false;
                return requireStock ? Number(m.currentStock || 0) > 0 : true;
            });

        const stockMat = findPaperMat(true);
        const catalogMat = stockMat || findPaperMat(false);
        const autoCode = `VT-GIAY-BOI-${paperCode}-${dims.formattedMm}`;
        const autoName = `Giấy bồi ${paperCode} (${gsm}gsm) khổ ${dims.formattedMm}mm`;

        const materialCode = catalogMat?.materialId || catalogMat?.code || autoCode;
        const materialName = catalogMat?.name || autoName;
        const materialId = catalogMat?.id || materialCode;

        return {
            originalMaterialId: materialId,
            plannedMaterialId: materialId,
            materialCode,
            materialName,
            materialCategory: 'PAPER',
            subType: `Giấy bồi ${paperCode}`,
            isPrint: false,
            sheetsRequired,
            wastageSheets,
            totalSheets,
            unit: catalogMat?.unit || 'Tờ',
            _gsm: String(catalogMat?.gsm || gsm),
            _size: dims.formattedMm,
            widthMm: dims.rawWidthMm,
            lengthMm: dims.rawLengthMm,
            isAutoGenerated: !catalogMat,
            matchSource: stockMat ? 'STOCK_EXACT' : catalogMat ? 'CATALOG_ZERO_STOCK' : 'AUTO_GENERATED',
            availableStock: Number(catalogMat?.currentStock || 0),
            splitReason: `Giấy bồi ${paperCode} theo khổ giấy in ${dims.formattedMm}mm`,
            isUserCustomized: false,
            autoRuleKey: `MOUNTING_BOI_GIAY_${paperCode}`,
        };
    });
}

/**
 * Plans the primary printing paper row from the PO's paper specification and sheet size.
 */
export function planMainPrintPaperMaterial(params: {
    sheetSize: string;
    paperSpec?: string | null;
    requiredSheets: number;
    wastageSheets: number;
    catalogMaterials?: CatalogMaterialItem[];
}): AutoPlannedMaterialRow | null {
    const dims = parseSheetDimensionsMm(params.sheetSize);
    if (!dims) return null;

    const rawSpec = (params.paperSpec || 'I300').trim();
    const normSpec = normalizeViText(rawSpec);
    const catalog = params.catalogMaterials || [];

    // Extract paper code & GSM (e.g. "Ivory 300" -> I300, 300; "C150" -> C150, 150)
    let paperCode = 'I300';
    let gsm = '300';
    const shortMatch = rawSpec.match(/\b([ICDFKB]\d{2,3})\b/i);
    const gsmMatch = rawSpec.match(/(\d{2,3})\s*(?:gsm|g)?/i);
    if (shortMatch) {
        paperCode = shortMatch[1].toUpperCase();
        gsm = paperCode.replace(/\D+/g, '') || '300';
    } else if (normSpec.includes('ivory')) {
        gsm = gsmMatch ? gsmMatch[1] : '300';
        paperCode = `I${gsm}`;
    } else if (normSpec.includes('couche')) {
        gsm = gsmMatch ? gsmMatch[1] : '150';
        paperCode = `C${gsm}`;
    } else if (normSpec.includes('duplex')) {
        gsm = gsmMatch ? gsmMatch[1] : '300';
        paperCode = `D${gsm}`;
    } else if (normSpec.includes('kraft')) {
        gsm = gsmMatch ? gsmMatch[1] : '250';
        paperCode = `K${gsm}`;
    } else if (normSpec.includes('bristol')) {
        gsm = gsmMatch ? gsmMatch[1] : '300';
        paperCode = `B${gsm}`;
    } else if (normSpec.includes('ford')) {
        gsm = gsmMatch ? gsmMatch[1] : '80';
        paperCode = `F${gsm}`;
    }

    const findMainPaper = (requireStock: boolean) =>
        catalog.find((m) => {
            if (m.category && m.category !== 'PAPER') return false;
            const text = normalizeViText(`${m.materialId || ''} ${m.code || ''} ${m.name || ''} ${m.subType || ''} ${m.specs || ''}`);
            if (text.includes('song ') || text.includes('chipboard') || text.includes('mang ')) return false;
            const hasGsm = String(m.gsm || '') === gsm || text.includes(gsm);
            const hasCode = text.includes(paperCode.toLowerCase());
            if (!hasGsm && !hasCode) return false;
            if (!matchesSheetDimensions(m, dims)) return false;
            return requireStock ? Number(m.currentStock || 0) > 0 : true;
        });

    const stockMat = findMainPaper(true);
    const catalogMat = stockMat || findMainPaper(false);

    // Strip out any post-press process clauses after comma/semicolon (e.g. "Ivory 300gsm, Cán màng mờ, Bồi sóng E" -> "Ivory 300gsm")
    const cleanPaperSegment =
        rawSpec
            .split(/[,;|•]/)[0]
            .replace(/\b(can mang.*|boi song.*|boi giay.*|ep kim.*|phu uv.*)$/i, '')
            .trim() || paperCode;

    const autoCode = `VT-GIAY-${paperCode}-${dims.formattedMm}`;
    const autoName = `Giấy in ${cleanPaperSegment} khổ ${dims.formattedMm}mm`;

    const materialCode = catalogMat?.materialId || catalogMat?.code || autoCode;
    const rawCatName = String(catalogMat?.name || '');
    const isPollutedCatName =
        rawCatName.toLowerCase().includes('cán màng') ||
        rawCatName.toLowerCase().includes('bồi sóng');
    const materialName = catalogMat && !isPollutedCatName ? catalogMat.name : autoName;
    const materialId = catalogMat?.id || materialCode;

    const sheetsRequired = Math.max(0, Math.round(params.requiredSheets));
    const wastageSheets = Math.max(0, Math.round(params.wastageSheets));
    const totalSheets = sheetsRequired + wastageSheets;

    return {
        originalMaterialId: materialId,
        plannedMaterialId: materialId,
        materialCode,
        materialName,
        materialCategory: 'PAPER',
        subType: `Giấy in ${paperCode}`,
        isPrint: true,
        sheetsRequired,
        wastageSheets,
        totalSheets,
        unit: catalogMat?.unit || 'Tờ',
        _gsm: String(catalogMat?.gsm || gsm),
        _size: dims.formattedMm,
        widthMm: dims.rawWidthMm,
        lengthMm: dims.rawLengthMm,
        isAutoGenerated: !catalogMat,
        matchSource: stockMat ? 'STOCK_EXACT' : catalogMat ? 'CATALOG_ZERO_STOCK' : 'AUTO_GENERATED',
        availableStock: Number(catalogMat?.currentStock || 0),
        splitReason: `Giấy in chính bài bình khổ ${dims.formattedMm}mm`,
        isUserCustomized: false,
        autoRuleKey: 'MAIN_PRINT_PAPER',
    };
}

export function inferMaterialAutoRuleKey(row: any): string | undefined {
    if (row?.autoRuleKey) return String(row.autoRuleKey);
    if (row?.isPrint) return 'MAIN_PRINT_PAPER';
    const code = String(
        row?.materialCode || row?.material?.materialId || row?.plannedMaterialId || row?.materialId || ''
    ).toUpperCase();
    const cat = String(row?.materialCategory || row?.material?.category || '').toUpperCase();
    const name = normalizeViText(String(row?.materialName || row?.material?.name || row?.name || ''));
    if (
        cat === 'FILM' ||
        code.startsWith('VT-MANG-') ||
        name.includes('mang bopp') ||
        name.includes('mang metalize') ||
        name.includes('mang nhung')
    ) {
        if (code.includes('-MET-') || name.includes('metalize')) return 'LAMINATION_MANG_METALIZE';
        if (code.includes('-NHUNG-') || name.includes('nhung')) return 'LAMINATION_MANG_NHUNG';
        if (code.includes('-BONG-') || name.includes('bong')) return 'LAMINATION_MANG_BONG';
        return 'LAMINATION_MANG_MO';
    }
    const songMatch = code.match(/^VT-SONG-([A-Z0-9]+)-/) || name.match(/song\s+([ebcf]|ab|bc)\b/i);
    if (songMatch) {
        return `MOUNTING_BOI_SONG_${songMatch[1].toUpperCase()}`;
    }
    const chipMatch = code.match(/^VT-CHIP-(\d+)-/) || (name.includes('chipboard') && name.match(/(\d{3,4})/));
    if (chipMatch) {
        return `MOUNTING_BOI_CHIPBOARD_${chipMatch[1]}`;
    }
    const boiGiayMatch = code.match(/^VT-GIAY-BOI-([A-Z0-9]+)-/);
    if (boiGiayMatch) {
        return `MOUNTING_BOI_GIAY_${boiGiayMatch[1].toUpperCase()}`;
    }
    if (code.startsWith('VT-GIAY-') && !code.startsWith('VT-GIAY-BOI-')) {
        return 'MAIN_PRINT_PAPER';
    }
    return undefined;
}

/**
 * Synchronizes the complete list of Material Plans & BOM Items for a Production Order,
 * while strictly preserving any row that the Production Planner has customized (`isUserCustomized === true`)
 * or any extra row already saved in the database (`id` present with no autoRuleKey).
 */
export function buildAutoPlannedMaterialsAndBom(params: {
    sheetSize: string;
    paperSpec?: string | null;
    requiredSheets: number;
    wastageSheets: number;
    processes?: string[];
    specification?: string | null;
    finishingSpecs?: Record<string, any> | null;
    orderSources?: Array<Record<string, any>>;
    notes?: string | null;
    catalogMaterials?: CatalogMaterialItem[];
    existingMaterialPlans?: any[];
    existingBomItems?: any[];
}): {
    materialPlans: AutoPlannedMaterialRow[];
    bomItems: AutoPlannedBomRow[];
    suggestedFinishingSpecs: Partial<{
        maMang: string;
        khoMang: string;
        slMangM: string;
        boi: string;
    }>;
} {
    const existingPlans = Array.isArray(params.existingMaterialPlans) ? params.existingMaterialPlans : [];
    const existingBoms = Array.isArray(params.existingBomItems) ? params.existingBomItems : [];

    const autoGeneratedPlans: AutoPlannedMaterialRow[] = [];
    const suggestedFinishingSpecs: Partial<{
        maMang: string;
        khoMang: string;
        slMangM: string;
        boi: string;
    }> = {};

    // 1. Main Print Paper
    const mainPaper = planMainPrintPaperMaterial({
        sheetSize: params.sheetSize,
        paperSpec: params.paperSpec,
        requiredSheets: params.requiredSheets,
        wastageSheets: params.wastageSheets,
        catalogMaterials: params.catalogMaterials,
    });
    if (mainPaper) {
        autoGeneratedPlans.push(mainPaper);
    }

    // 2. Lamination Film (Cán màng)
    const lamReq = detectLaminationRequirement({
        processes: params.processes,
        specification: params.specification,
        finishingSpecs: params.finishingSpecs,
        orderSources: params.orderSources,
        notes: params.notes,
    });
    if (lamReq.required) {
        const filmPlan = planLaminationFilmMaterial({
            sheetSize: params.sheetSize,
            requiredSheets: params.requiredSheets,
            wastageSheets: params.wastageSheets,
            filmType: lamReq.filmType,
            sides: lamReq.sides,
            catalogMaterials: params.catalogMaterials,
        });
        if (filmPlan) {
            autoGeneratedPlans.push(filmPlan);
            const dirLabel = filmPlan.feedDirection === 'HORIZONTAL' ? 'Khổ ngang -5mm' : 'Khổ dọc -5mm';
            suggestedFinishingSpecs.maMang = `${filmPlan.materialCode} (${filmPlan.materialName})`;
            suggestedFinishingSpecs.khoMang = `${filmPlan.rollWidthMm}mm (${dirLabel}, bước cắt ${filmPlan.feedLengthMm}mm)`;
            suggestedFinishingSpecs.slMangM = `${filmPlan.linearMeters}m (~${filmPlan.weightKg}kg / ${filmPlan.totalSheets} tờ)`;
        }
    }

    // 3. Mounting Substrates (Bồi sóng, Bồi giấy, Bồi chipboard)
    const mountingSpecs = detectMountingRequirements({
        processes: params.processes,
        specification: params.specification,
        finishingSpecs: params.finishingSpecs,
        orderSources: params.orderSources,
        notes: params.notes,
    });
    if (mountingSpecs.length > 0) {
        const mountingPlans = planMountingSubstrateMaterials({
            sheetSize: params.sheetSize,
            requiredSheets: params.requiredSheets,
            wastageSheets: params.wastageSheets,
            mountingSpecs,
            catalogMaterials: params.catalogMaterials,
        });
        autoGeneratedPlans.push(...mountingPlans);
        if (mountingPlans.length > 0) {
            suggestedFinishingSpecs.boi = mountingPlans
                .map((m) => `${m.materialCode} (${m.materialName})`)
                .join(' + ');
        }
    }

    // Merge with existingMaterialPlans: preserve `isUserCustomized` rows and link existing DB `id`s
    const mergedPlans: AutoPlannedMaterialRow[] = [];
    const handledRuleKeys = new Set<string>();
    const consumedExistingSet = new Set<any>();

    for (const autoRow of autoGeneratedPlans) {
        const key = autoRow.autoRuleKey || autoRow.materialCode;
        const matchingExisting = existingPlans.find((ex) => {
            const exKey = inferMaterialAutoRuleKey(ex);
            return (
                (exKey && exKey === key) ||
                (key === 'MAIN_PRINT_PAPER' && ex?.isPrint) ||
                (ex?.materialCode && ex.materialCode === autoRow.materialCode)
            );
        });
        if (matchingExisting) {
            consumedExistingSet.add(matchingExisting);
            if (matchingExisting.isUserCustomized) {
                mergedPlans.push({
                    ...matchingExisting,
                    autoRuleKey: matchingExisting.autoRuleKey || key,
                });
            } else {
                mergedPlans.push({
                    ...autoRow,
                    ...(matchingExisting.id ? { id: matchingExisting.id } : {}),
                } as AutoPlannedMaterialRow);
            }
        } else {
            mergedPlans.push(autoRow);
        }
        handledRuleKeys.add(key);
    }

    // Keep any extra manually added or DB-persisted custom rows from existingMaterialPlans
    for (const ex of existingPlans) {
        if (consumedExistingSet.has(ex)) continue;
        const exKey = inferMaterialAutoRuleKey(ex);
        if (ex?.isUserCustomized || (ex?.id && (!exKey || !handledRuleKeys.has(exKey)))) {
            if (!mergedPlans.includes(ex)) {
                mergedPlans.push(ex);
            }
        }
    }

    // Build synchronized BOM items from mergedPlans + preserve user-customized BOM items
    const autoBoms: AutoPlannedBomRow[] = mergedPlans.map((plan) => {
        const wastagePct =
            plan.sheetsRequired > 0
                ? Number(((plan.wastageSheets / plan.sheetsRequired) * 100).toFixed(1))
                : 0;
        const specParts = [
            plan._gsm ? `${plan._gsm}` : '',
            plan._size ? `Khổ ${plan._size}mm` : '',
            plan.linearMeters ? `~${plan.linearMeters}m (${plan.weightKg || 0}kg)` : '',
        ].filter(Boolean);
        const matchingExistingBom = existingBoms.find(
            (b) =>
                (plan.autoRuleKey && inferMaterialAutoRuleKey(b) === plan.autoRuleKey) ||
                (b?.materialCode && b.materialCode === plan.materialCode) ||
                (b?.materialId && b.materialId === (plan.plannedMaterialId || plan.materialCode))
        );
        return {
            ...(matchingExistingBom?.id ? { id: matchingExistingBom.id } : {}),
            materialId: plan.plannedMaterialId || plan.materialCode,
            materialCode: plan.materialCode,
            materialName: plan.materialName,
            materialCategory: plan.materialCategory || 'PAPER',
            specification: specParts.join(' • '),
            unit: plan.unit || 'Tờ',
            quantity: plan.sheetsRequired,
            qtyRequired: plan.sheetsRequired,
            wastagePercent: wastagePct,
            totalQuantity: plan.totalSheets,
            _gsm: plan._gsm,
            _size: plan._size,
            linearMeters: plan.linearMeters,
            weightKg: plan.weightKg,
            notes: plan.splitReason,
            isAutoGenerated: plan.isAutoGenerated,
            isUserCustomized: Boolean(plan.isUserCustomized),
            autoRuleKey: plan.autoRuleKey,
        };
    });

    // Preserve extra user-customized or DB-persisted BOM rows
    for (const exBom of existingBoms) {
        const exBomKey = inferMaterialAutoRuleKey(exBom);
        if (exBom?.isUserCustomized || (exBom?.id && (!exBomKey || !handledRuleKeys.has(exBomKey)))) {
            const alreadyIn = autoBoms.some(
                (b) =>
                    (exBomKey && b.autoRuleKey === exBomKey) ||
                    (exBom.materialCode && b.materialCode === exBom.materialCode) ||
                    b.materialId === exBom.materialId
            );
            if (!alreadyIn) {
                autoBoms.push(exBom);
            }
        }
    }

    return {
        materialPlans: mergedPlans,
        bomItems: autoBoms,
        suggestedFinishingSpecs,
    };
}
